/*
 * product-configurator-progress.js — list-type accordion cascade +
 * page-wide single-open + "all selections made" signal for the
 * Heirest-style PDP configurator.
 *
 * Figma dev annotation (node 11012:185341, "PDP/ all selections made"):
 *   "Exception to the rule of one accordion open at a time. If the
 *   selector is a list type, once the list item has been clicked, the
 *   accordion can close and the next one open. When the last selection
 *   is made, and if the last accordion is a list type accordion, then
 *   all accordions can close to form this summary view, and the ATC
 *   button changes to [blue]."
 *
 * Scope: only list-layout rows (Carat / Size / compound-accordion parts
 * — anything rendered by snippets/product-option-accordion.liquid with
 * layout: 'list', detectable at runtime via the
 * .product-option-accordion__body--list child, no markup changes
 * needed) trigger the auto-close-and-advance cascade. Swatch-layout
 * accordions (Metal, Stone, Shape) are untouched by the cascade itself
 * — Metal's standalone accordion (blocks/product-group-selector.liquid,
 * not part of <accordion-group>) is closed by the page-wide single-open
 * listener below, and swept closed again when the cascade completes.
 *
 * The completion signal (`pdp:selections-complete`) is one-way for the
 * rest of the page's life — reopening an accordion afterward to change
 * a value does not un-fire it (assets/product-purchase-form.js and
 * assets/sticky-add-to-cart.js both listen for it to flip their ATC
 * button to the "ready" blue state via a `.is-ready` class).
 */

let selectionsComplete = false;

function isListLayout(details) {
  return !!details.querySelector('.product-option-accordion__body--list');
}

function nextDetailsInGroup(details) {
  const group = details.closest('accordion-group');
  if (!group) return null;
  let sibling = details.nextElementSibling;
  while (sibling && sibling.tagName !== 'DETAILS') {
    sibling = sibling.nextElementSibling;
  }
  return sibling && group.contains(sibling) ? sibling : null;
}

/**
 * Only one configurator accordion open at a time — including Metal,
 * which lives outside the purchase-form <accordion-group>. Closing
 * (open → false) is always allowed so the final summary view can
 * collapse every row.
 */
function handleToggle(event) {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  if (!details.classList.contains('product-option-accordion')) return;
  if (!details.open) return;

  document.querySelectorAll('details.product-option-accordion').forEach((other) => {
    if (other !== details && other.open) other.open = false;
  });
}

function completeSelections() {
  if (selectionsComplete) return;
  selectionsComplete = true;
  document
    .querySelectorAll('details.product-option-accordion')
    .forEach((details) => {
      details.open = false;
    });
  // Stop Metal's open-by-default from re-expanding on breakpoint changes
  // after the summary view is reached.
  document.querySelectorAll('accordion-custom').forEach((el) => {
    if (!el.querySelector('details.product-option-accordion')) return;
    el.removeAttribute('open-by-default-on-desktop');
    el.removeAttribute('open-by-default-on-mobile');
  });
  document.dispatchEvent(new CustomEvent('pdp:selections-complete'));
}

function handleChange(event) {
  if (selectionsComplete) return;

  const input = event.target;
  if (!input?.classList?.contains('product-option-accordion__input') || !input.checked) {
    return;
  }

  const details = input.closest('details.product-option-accordion');
  if (!details || !isListLayout(details)) return;

  details.open = false;

  const next = nextDetailsInGroup(details);
  if (next) {
    next.open = true;
    // Programmatic opens skip :focus-within, which gates the open/close
    // transition in accordion-custom-component.liquid — focus the summary
    // so the cascade animates instead of snapping open.
    next.querySelector(':scope > summary')?.focus({ preventScroll: true });
  } else {
    completeSelections();
  }
}

document.addEventListener('toggle', handleToggle, true);
document.addEventListener('change', handleChange);
